// Departman bazlı soru havuzu (Kalite Yönetimi › Soru Havuzu).
//
// "Departman", SOP ve eğitim atamalarında kullanılan grup/rol değeridir
// (bkz. constants/employeeGroups.js): SOPs.group, IndicationEducations.group ve
// Employees.role aynı değerleri taşır. 'Hepsi' havuzu her departmana sorulur.
//
// Her SOP/eğitim okumasının sonunda çalışanın departman havuzundan (+ 'Hepsi')
// rastgele sorular çekilir; her denemede seçim yeniden yapılır.
//
// Koleksiyonlar:
//   QuestionPool          — { group, question, options[], correctAnswer, explanation, isActive }
//   QuizAttempts          — her denemenin sonucu (raporlama için)
//   Settings/questionPool — { questionCount, passScore }

import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { db } from '../config/firebaseConfig';

export const QUESTION_POOL_COLLECTION = 'QuestionPool';
export const QUIZ_ATTEMPTS_COLLECTION = 'QuizAttempts';
export const COMMON_GROUP = 'Hepsi';

export const QUIZ_SOURCE = { SOP: 'SOP', EDUCATION: 'Eğitim' };

export const DEFAULT_QUIZ_SETTINGS = { questionCount: 5, passScore: 70 };
export const MIN_OPTIONS = 2;
export const MAX_OPTIONS = 6;

const settingsRef = () => doc(db, 'Settings', 'questionPool');

const clamp = (value, min, max, fallback) => {
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? Math.min(max, Math.max(min, parsed)) : fallback;
};

export const normalizeQuizSettings = (settings = {}) => ({
  questionCount: clamp(settings.questionCount, 1, 50, DEFAULT_QUIZ_SETTINGS.questionCount),
  passScore: clamp(settings.passScore, 1, 100, DEFAULT_QUIZ_SETTINGS.passScore),
});

export const fetchQuizSettings = async () => {
  const snapshot = await getDoc(settingsRef());
  return normalizeQuizSettings(snapshot.exists() ? snapshot.data() : {});
};

export const saveQuizSettings = (settings) =>
  setDoc(settingsRef(), { ...normalizeQuizSettings(settings), updatedAt: serverTimestamp() }, { merge: true });

// Fisher–Yates: diziyi kopyalayıp karıştırır
export const shuffle = (items) => {
  const result = [...items];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

// Şıkları karıştırır ve doğru cevabın yeni sırasını hesaplar
const shuffleOptions = (question) => {
  const tagged = question.options.map((text, index) => ({ text, correct: index === question.correctAnswer }));
  const mixed = shuffle(tagged);
  return {
    ...question,
    options: mixed.map((option) => option.text),
    correctAnswer: mixed.findIndex((option) => option.correct),
  };
};

const isUsable = (question) =>
  question.isActive !== false &&
  Array.isArray(question.options) &&
  question.options.length >= MIN_OPTIONS &&
  Number.isInteger(question.correctAnswer) &&
  question.correctAnswer >= 0 &&
  question.correctAnswer < question.options.length;

/**
 * Çalışanın departman havuzundan (+ ortak havuz) rastgele sorular çeker.
 * Havuz boşsa boş dizi döner; çağıran taraf bu durumda sınavı atlar.
 *
 * @returns {Promise<{questions: object[], passScore: number}>}
 */
export const drawQuizQuestions = async (group) => {
  const groups = [...new Set([group, COMMON_GROUP].filter(Boolean))];
  const [settings, snapshot] = await Promise.all([
    fetchQuizSettings(),
    getDocs(query(collection(db, QUESTION_POOL_COLLECTION), where('group', 'in', groups))),
  ]);

  const pool = snapshot.docs.map((item) => ({ id: item.id, ...item.data() })).filter(isUsable);
  const questions = shuffle(pool).slice(0, settings.questionCount).map(shuffleOptions);
  return { questions, passScore: settings.passScore };
};

// answers: { [questionId]: seçilen şık sırası }
export const gradeQuiz = (questions, answers, passScore) => {
  const correctCount = questions.filter((q) => answers[q.id] === q.correctAnswer).length;
  const score = questions.length > 0 ? Math.round((correctCount / questions.length) * 100) : 0;
  return { correctCount, score, passed: score >= passScore };
};

// Deneme kaydı raporlama içindir; yazılamaması sınav akışını engellemez.
export const recordQuizAttempt = async (attempt) => {
  try {
    await addDoc(collection(db, QUIZ_ATTEMPTS_COLLECTION), { ...attempt, attemptedAt: serverTimestamp() });
  } catch (error) {
    console.error('Sınav denemesi kaydedilemedi:', error);
  }
};
